/**
 * PHASE 10 — settings panel.
 *
 * Display toggles (constellation lines / deep-sky / labels), compass
 * calibration controls, and a location readout with manual override.
 * Pure DOM shell: all state flows in via sync(), decisions go out via
 * callbacks, so main.ts stays the single source of truth.
 */

import type { Preferences } from "../settings/preferences";
import type { GeoLocation } from "../location/types";

export type SettingsToggleKey = "showConstellationLines" | "showDeepSky" | "showLabels";

export interface SettingsPanelOptions {
  onToggle: (key: SettingsToggleKey, value: boolean) => void;
  onCalibrate: () => void;
  onResetCalibration: () => void;
  onSetManual: (latDeg: number, lonDeg: number) => void;
}

const TOGGLES: readonly { key: SettingsToggleKey; label: string }[] = [
  { key: "showConstellationLines", label: "Constellation lines" },
  { key: "showDeepSky", label: "Deep-sky objects" },
  { key: "showLabels", label: "Sky labels" },
];

export class SettingsPanel {
  private toggles = new Map<SettingsToggleKey, HTMLInputElement>();
  private calibrationEl: HTMLElement;
  private locationEl: HTMLElement;
  private latInput: HTMLInputElement;
  private lonInput: HTMLInputElement;

  constructor(
    private root: HTMLElement,
    options: SettingsPanelOptions,
  ) {
    for (const { key, label } of TOGGLES) {
      const labelEl = document.createElement("label");
      labelEl.className = "setting-row";
      const input = document.createElement("input");
      input.type = "checkbox";
      input.addEventListener("change", () => options.onToggle(key, input.checked));
      labelEl.append(input, document.createTextNode(label));
      root.querySelector<HTMLElement>(".settings-display")?.appendChild(labelEl);
      this.toggles.set(key, input);
    }
    this.calibrationEl = root.querySelector<HTMLElement>(".settings-calibration") ?? document.createElement("div");
    this.locationEl = root.querySelector<HTMLElement>(".settings-location") ?? document.createElement("div");
    this.latInput = root.querySelector<HTMLInputElement>(".loc-lat") ?? document.createElement("input");
    this.lonInput = root.querySelector<HTMLInputElement>(".loc-lon") ?? document.createElement("input");
    root.querySelector<HTMLButtonElement>(".settings-calibrate")?.addEventListener("click", () => options.onCalibrate());
    root.querySelector<HTMLButtonElement>(".settings-reset-calibration")?.addEventListener("click", () => options.onResetCalibration());
    root.querySelector<HTMLButtonElement>(".loc-apply")?.addEventListener("click", () => {
      const lat = Number(this.latInput.value);
      const lon = Number(this.lonInput.value);
      if (Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180) {
        options.onSetManual(lat, lon);
      }
    });
  }

  get isOpen(): boolean {
    return this.root.style.display !== "none";
  }

  open(): void {
    this.root.style.display = "block";
    this.latInput.value = "";
    this.lonInput.value = "";
  }

  close(): void {
    this.root.style.display = "none";
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  /** Push the current application state into the panel readouts. */
  sync(prefs: Preferences, calibrationOffsetDeg: number, location: GeoLocation): void {
    for (const { key } of TOGGLES) {
      const input = this.toggles.get(key);
      if (input && input.checked !== prefs[key]) input.checked = prefs[key];
    }
    this.calibrationEl.textContent = `Offset: ${calibrationOffsetDeg.toFixed(1)}°`;
    const src = location.source === "gps" ? "GPS" : location.source === "manual" ? "manual" : "default";
    this.locationEl.textContent = `${location.latDeg.toFixed(4)}°, ${location.lonDeg.toFixed(4)}° (${src})`;
  }
}